import * as Sentry from '@sentry/vue'
import store from '@/vuex/store'
import router from '@/router'
import Config from '@/config'
// import EventBus from '@/eventBus'
import slideApi from '@/api/slide'
import { createPptActiveViewMachine } from '@/services/pptActiveViewMachine'
import UnleashService from '@/services/unleash'
import officeConstants from '@/constant/office'
import { createDebug, FRAME_ID } from '@/utils/debug'
import { mountFrameIdBadge } from '@/utils/frame-badge'
import {
  isDesktopOfficeHost,
  isPowerPointApiSupported,
  queryActiveView,
  readSelectedSlideIdBounded,
  SELECTED_SLIDE_READ_TIMEOUT_MS,
} from '@/services/office/helpers'
import { resizeAddinToFillSlide } from '@/services/office/resize'
import {
  readSlideBindingFromHostSlide,
  clearSlideBindingFromHostSlide,
  writeSlideBindingToHostSlide,
  toNumericId,
} from '@/services/office/insert-slides'
// Read only to explain a false auto-start verdict — it is the same probe the store getter makes.
import { canReadSlideTags } from '@/services/office/slide-tags'
import {
  isActiveSlideBoundToThisFrame,
  startTagSlideFocusPolling,
  stopTagSlideFocusPolling,
  registerOwnBindingReader,
} from '@/services/office/current-slide'
import { canUsePptSlideSelector } from '@/business-logic/ppt-slide-selector-gate'
import { ROUTE_NAMES } from '@/constant/routes'
import { isPptOnlineAddin } from '@/utils/ppt-online.utils'
import {
  savePptOnlineBinding,
  loadPptOnlineBinding,
  savePptCommonSlideBinding,
  loadPptCommonSlideBinding,
} from '@/services/office/ppt-online-binding'
import {
  publishShowCurrentSlideId,
  readShowCurrentSlideId,
  followBroadcastCurrentSlide,
} from '@/services/office/ppt-online-current-slide'
import { appendPreviousSlideId, withoutSlideIdHistory } from '@/services/office/slide-binding-history'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:auto-start'
const log = createDebug('ppt:auto-start')
// AHA-46192 default-presentation flow. Toggle with: localStorage.debug = 'aha:ppt:default-presentation'
const logDefault = createDebug('ppt:default-presentation')
// AHA-43111: cold-load routing decision. On PowerPoint Online, Slide Show reloads the
// taskpane as a brand-new session, so this trace is how we see WHICH binding source
// resolved the frame — or that none did and it fell through to the presentation list.
// Toggle with: localStorage.debug = 'aha:ppt:load-route' (or 'aha:ppt:*' for everything).
const logRoute = createDebug('ppt:load-route')
// AHA-46751 present-run remap. Same namespace as slide-binding-history on purpose, so
// `localStorage.debug = 'aha:ppt:slide-remap'` traces one story end to end: what the remap API
// was asked, what it answered, whether that answer was stored, and what a later lookup found.
const logRemap = createDebug('ppt:slide-remap')

let initOffice = false
let viewMachine = null
let viewResizeObserver = null
let activeViewHandler = null
// Set right before each synchronous handleView() call so onEnterRead can log which
// signal source (ActiveViewChanged / ResizeObserver / init-query) triggered it.
let lastViewSource = 'unknown'
// Slide-focus polling lives in services/office/current-slide now. AHA-46751 removed the old poll
// outright rather than keeping it as a fallback: it tracked a savedHostPptSlideId captured from
// the DOCUMENT-GLOBAL selection on entering read view, so in a deck with several add-in slides
// every frame captured the slide being shown and believed it was its own — the false claim that
// had them all fighting over the audience. There is no host left that needs it: auto-start now
// requires slide-tag support (featureToggles/canUseAddinPowerpointAutoStart), and a frame with
// tags can ask the slide what it is bound to instead of guessing.
const AUTO_START_ACTION = 'pptAddin/autoStartActiveSlideFromView'
// Upper bound on how long add-in load waits for UnleashService.ready before routing
// anyway. `ready` only resolves on the client's 'ready' event, so a stalled proxy must
// not be allowed to hang load-time routing (incl. the flag-off auto-start path).
//
// Raised from 3s: in the desktop WebView the toggle fetch is not the first request — under the
// control variant of NEW_UNLEASH_MECHANISM, start() only runs after getCurrentUserAction
// resolves (vuex/actions/currentUser.js), so this budget has to cover TWO sequential
// round trips on a cold connection. 3s was routinely losing that race, and the cost is not a
// slow start but a WRONG one: detectActiveViewChange() reads the flag exactly once and the
// frame is then on the legacy path for its whole life.
//
// The cost of raising it is paid only when Unleash is genuinely unreachable — every add-in
// frame then sits this long before its landing route settles. Frames wait in parallel, so a
// bulk-added deck pays it once, not once per frame.
const UNLEASH_READY_TIMEOUT_MS = 8000
// AHA-43111: on PowerPoint for the web, inserting several add-in frames at once means
// Office.onReady fires for some frames but not others. A laggard frame must not initialise
// against an empty Office.context (host/platform undefined) — that trips the host guard and
// strands it — so we poll for a ready context on this interval, up to this ceiling, then give
// up (resolving without init) so an awaited loadOffice() can never hang.
const OFFICE_READY_POLL_INTERVAL_MS = 500
const OFFICE_READY_MAX_WAIT_MS = 15000
// let ws

// AHA-46192: the cross-frame "last selection" lives in a PowerPoint presentation
// TAG (PowerPointApi 1.3), NOT in Office document.settings or localStorage.
// - document.settings is keyed per add-in INSTANCE and is only shared between frames
//   created by DUPLICATING a slide (office-js #2765) — not across inserted frames.
// - localStorage is origin-scoped, so it leaks across every .pptx open in the browser.
// A presentation tag is document-wide on BOTH web and desktop and persists inside the
// .pptx itself, so it is exactly "the last presentation picked in THIS document" and is
// readable from any add-in frame. Tag keys are case-insensitive (stored uppercased).
const LAST_SELECTED_TAG_KEY = 'AHALASTSELECTEDPRESENTATION'

// AHA-46192: the last-selected store uses the TagCollection API, which was added in
// PowerPointApi 1.3. window.PowerPoint.run alone only proves 1.1, so also confirm the
// host supports 1.3 — on an older PowerPoint we skip cleanly instead of letting the
// tag calls throw inside PowerPoint.run and relying on the catch to swallow them.
const canUsePresentationTags = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  return isPowerPointApiSupported('1.3')
}

// AHA-46192: gate the "default to last selected presentation" fallback.
const isPptDefaultPresentationEnabled = () => {
  return Boolean(store.getters['featureToggles/canUsePptDefaultPresentation'])
}

// A signed-out add-in frame lands on /pages/login|register; the add-in load flow must
// never hijack those. Centralised so every early-out shares one check.
const isAuthPage = () => {
  const { href } = window.location
  return href.indexOf('/pages/login') !== -1 || href.indexOf('/pages/register') !== -1
}

// AHA-43111 review: handleDataAfterLoadOffice() (and therefore this function) can run
// from App.vue's mounted() hook before the router's first navigation has resolved —
// every candidate route (/pages/login, /pages/oauth, /pages/register) is lazy-loaded,
// so `transitionTo` cannot finish synchronously. In that window router.currentRoute is
// still Vue Router 3's START route (`query: {}`) even though the real browser URL still
// carries invite-code/client_id. Fall back to parsing window.location.search so that
// race doesn't silently drop the forwarded query.
const currentQuery = () => {
  const routeQuery = router.currentRoute?.query
  if (routeQuery && Object.keys(routeQuery).length > 0) {
    return routeQuery
  }
  return Object.fromEntries(new URLSearchParams(window.location.search))
}

// AHA-43111: did this cold load send the frame anywhere? Needed because router.currentRoute
// CANNOT answer it: every route here is lazy-loaded, so a router.replace() issued by one of
// the branches below has not resolved — and currentRoute has not moved off the landing route —
// by the time the load chain's .finally runs a tick later. Keying the landing-route handover
// off currentRoute alone therefore overwrote the editor navigation the frame had just started
// and dropped it on the presentation list, i.e. exactly the bug the landing route removes.
let coldLoadNavigated = false

// Single chokepoint so every navigation this module makes is recorded, whichever branch it
// came from. Behaviourally a straight pass-through to router.replace.
const replaceRoute = (location) => {
  coldLoadNavigated = true
  return router.replace(location)
}

const redirectWebAddinToDeviceGrant = () => {
  // AHA-43111 review: this function's purpose is "a SIGNED-OUT web add-in must go to
  // the device grant" — encode that directly instead of inferring it from the URL.
  // handleDataAfterLoadOffice() also runs right after a successful sign-in (LoginV3,
  // RegisterV3, SSOForm), fired synchronously after an ASYNC router.push whose target
  // route hasn't resolved yet — window.location.href is still the login/register page
  // at that point, so isAuthPage() alone would wrongly bounce an already-authenticated
  // user back to the device grant. Both checks are needed: this one covers the
  // post-login race, isAuthPage() keeps the add-in load path (never authenticated yet)
  // from redirecting off of unrelated pages.
  if (store.state.isAuthenticated) return false
  if (!isPptOnlineAddin()) return false
  if (!Config.powerpointOAuthClientId) return false
  if (!isAuthPage()) return false
  // Forward the current query (invite-code, trial/bulk-license params, client_id) —
  // dropping it strands an invited/trial user without that context on the device
  // grant page. Guard the returned promise so a guard-side redirect/abort doesn't
  // surface as an unhandled rejection under Vue Router 3.
  const navigation = replaceRoute({ path: '/pages/oauth', query: currentQuery() })
  if (navigation && typeof navigation.catch === 'function') {
    navigation.catch((error) => {
      if (error && error.name === 'NavigationDuplicated') return
      Sentry.captureException(error)
    })
  }
  return true
}

// AHA-46192: the tag stores ONLY the presentationId (a plain string), never the
// {presentationId, slideId} / {activePresentationId} shapes — the fallback always
// opens the whole presentation, so a slideId would only re-pin another frame's slide.
const saveLastSelectedPresentation = async (presentationId) => {
  if (!canUsePresentationTags()) {
    return
  }
  try {
    await window.PowerPoint.run(async (context) => {
      const { tags } = context.presentation
      if (presentationId) {
        tags.add(LAST_SELECTED_TAG_KEY, String(presentationId))
      } else {
        // delete() is a no-op when the key is absent, so no existence check is needed.
        tags.delete(LAST_SELECTED_TAG_KEY)
      }
      await context.sync()
    })
    logDefault('saved last-selected tag =', presentationId)
  } catch (error) {
    logDefault('save last-selected tag failed', error)
  }
}

// AHA-46192: forget the document's last-selected presentation. Called when the user
// deliberately navigates OUT of the editor (editor-header back), which full-reloads
// onto a non-/presentation/ URL. Clearing the tag first means the reload's fallback
// finds nothing and stays put, so the user can browse other presentations instead of
// being bounced straight back into the one they just left. Flag-gated + awaitable so
// the caller can finish the tag write before triggering the reload.
const clearLastSelectedPresentation = async () => {
  // AHA-43111: the back paths that call this (selector Back, the 400 bounce) don't go through
  // saveDataToSlidePowerPoint(null), so clear the web read-view bridge here too — otherwise a
  // stale localStorage binding would route a later Slide Show back into a presentation the user
  // deliberately left. Cleared BEFORE the default-presentation gate below: the bridge is driven
  // by the independent canUsePptOnline flag, so it must not hinge on canUsePptDefaultPresentation.
  savePptOnlineBinding(null)
  if (!isPptDefaultPresentationEnabled()) {
    return
  }
  logDefault('clearing last-selected tag (editor back)')
  await saveLastSelectedPresentation(null)
}

const loadLastSelectedPresentation = async () => {
  if (!canUsePresentationTags()) {
    return null
  }
  try {
    let raw = null
    await window.PowerPoint.run(async (context) => {
      const tag = context.presentation.tags.getItemOrNullObject(LAST_SELECTED_TAG_KEY)
      tag.load('value,isNullObject')
      await context.sync()
      raw = tag.isNullObject ? null : tag.value
    })
    logDefault('loaded last-selected tag =', raw)
    return raw || null
  } catch (error) {
    logDefault('load last-selected tag failed', error)
    return null
  }
}

// A dataSlidePPT write is mirrored into two other stores so nothing can drift from it:
//  - AHA-46192: the global last-selected presentation TAG, so a blank frame can fall back to
//    it. Only the presentationId is mirrored (from either value shape); null clears it.
//  - AHA-46329: this frame's own host slide TAG, which outranks settings on load and is the
//    ONLY binding a cloned frame can read. Without this, a rebind through the ordinary editor
//    buttons (which write settings only) is silently reverted by the stale tag on the next
//    load, and clearing settings on "leave the editor" leaves the tag behind to trap the user
//    in the presentation they left. The selector opts out (mirrorToHostSlide: false): its
//    insert path has already tagged the host slide by id — robust against a selection that
//    PowerPoint moved onto a freshly inserted clone, which this selected-slide mirror is not.
//    NOT gated on canUsePptSlideSelector: the tag has to be written by the ordinary editor
//    buttons too, whatever the multi-select flag says, so a frame is never left with a
//    binding that only document.settings knows about. writeSlideBindingToHostSlide still
//    no-ops on a host without the slide-tag API (canReadSlideTags), so an unsupported
//    PowerPoint is unaffected.
// The remaining mirror is gated on its own flag, so flag-off / non-add-in behaviour is
// unchanged for it.
// Is PowerPoint currently in the Slide Show?
//
// Asked of the HOST, not of store.state.addinPPTActiveView: that mirror defaults to 'edit'
// (vuex/defaultStates/common.js) and is only filled in by an async getActiveViewAsync
// (resyncActiveView('init-query') on the auto-start path, reCheckActiveView() on the legacy one)
// that races the cold-load write this guard exists to hold back. On the Slide-Show entry reload —
// the exact path the comment below names as the corruption source — a frame that reaches the
// mirror before the view query lands reads 'edit' and writes. The mirror fails the other way too:
// the legacy ActiveViewChanged handler is flaky on Mac (office-js #102), and a mirror stuck at
// 'read' would make every later editor rebind skip the tag, leaving it stale until the next show
// resolves OTHER — a dead slide. Asking the host removes both directions.
//
// queryActiveView is a Common API and IS serviced in the Slide Show (see resize.js, which uses it
// for the same decision, for the same reason). It resolves null when the API is absent, so a
// non-add-in host falls through to the mirror branch exactly as before.
const isHostInReadView = async () => (await queryActiveView()) === 'read'

const mirrorDataSlideBinding = async (value, mirrorToHostSlide) => {
  // AHA-43111: mirror into the web read-view localStorage bridge (no-op off PowerPoint Online).
  // Sits alongside the tag mirrors so any binding write — including the null unbind — updates it.
  savePptOnlineBinding(value)
  if (isPptDefaultPresentationEnabled()) {
    const presentationId = value && (value.activePresentationId || value.presentationId)
    await saveLastSelectedPresentation(presentationId || null)
  }
  if (mirrorToHostSlide && await isHostInReadView()) {
    // AHA-46751: NEVER mirror during a Slide Show. writeSlideBindingToHostSlide resolves its
    // target with getSelectedDataAsync, which is DOCUMENT-global. In the editor that is sound —
    // the frame's own slide is the selected one — but in the Slide Show "selected" means "on
    // SCREEN", so a frame writing here stamps its binding onto whatever slide the presenter
    // happens to be showing and overwrites THAT slide's tag.
    //
    // Observed live: host slide 1 read back its own binding at the start of the show, then read
    // back the OTHER frame's binding after a navigation. The frame that genuinely owns slide 1
    // then correctly concluded the shown slide was not its own and stopped auto-starting — a
    // corrupted tag, diagnosed as a broken comparison.
    //
    // Only the tag is held back. document.settings is per-instance and stays correct, so the
    // frame keeps its binding and the next edit-view write re-mirrors it onto the right slide.
    log('skip host-slide tag mirror: in read view, selection is the shown slide, not this frame\'s')
  } else if (mirrorToHostSlide) {
    // AHA-43111: the tag mirror hands back the Common-API id of the slide it actually tagged,
    // which keys the per-slide read-view bridge so a deck with several distinct AhaSlides slides
    // resolves the right one in the Slide Show. Deriving the key from that same slide — rather
    // than a second getSelectedDataAsync — is what stops the key and the binding coming from
    // two different slides. The mirror resolves its target by selection, so an ill-timed
    // thumbnail click can still send BOTH to the wrong slide, but they can no longer disagree.
    const commonSlideId = await writeSlideBindingToHostSlide(value)
    savePptCommonSlideBinding(commonSlideId, value)
  }
}

const saveDataToSlidePowerPoint = async (key, value, { mirrorToHostSlide = true, keepSlideIdHistory = false } = {}) => {
  if (window.Office && window.Office.context && window.Office.context.host) {
    // AHA-46751: only the present-run remap may carry previousSlideIds, so it is stripped from
    // every other dataSlidePPT write. Those writes REPLACE the binding — back to the editor
    // (null), picking a different slide, binding a whole presentation, an insert re-baking a
    // clone's snapshot — and a history describing the binding they replaced would go on
    // vouching for slides this frame no longer owns: the ownership check would read a stale tag
    // as "mine" and this frame would take over another frame's slide.
    //
    // Enforced here rather than trusted to each call site because the failure is silent, and
    // because this is the funnel every one of them already goes through. Today they all build a
    // fresh object literal — so this is a guarantee against the caller that spreads an existing
    // binding, not a fix for one that does.
    const data = (key === 'dataSlidePPT' && !keepSlideIdHistory) ? withoutSlideIdHistory(value) : value
    if (key === 'dataSlidePPT') {
      await mirrorDataSlideBinding(data, mirrorToHostSlide)
    }
    window.Office.context.document.settings.set(key, data)
    if (!data) {
      window.Office.context.document.settings.remove(key)
      window.Office.context.document.settings.set(key, '')
      window.Office.context.document.settings.saveAsync(() => {
      })
    }
    return new Promise((resolve, reject) => {
      window.Office.context.document.settings.saveAsync((asyncResult) => {
        if (asyncResult.status === window.Office.AsyncResultStatus.Failed) {
          reject()
        } else {
          resolve()
        }
      })
    })
  }
  return null
}

// const addDataToSelectedSlide = async (dataNumber) => {
//   try {
//     await window.PowerPoint.run(async (context) => {
//       const selection = context.presentation.getSelectedSlides()
//       selection.load('items, count')
//       await context.sync()

//       if (selection.count === 0) {
//         console.log('No slide selected! Please select a slide or object on slide.')
//         return
//       }

//       const slide = selection.items[0]
//       slide.load('index, tags')
//       await context.sync()

//       const myData = { value: dataNumber, slideIndex: slide.index }
//       slide.tags.add('myData', JSON.stringify(myData))
//       await context.sync()

//       console.log(`Data ${dataNumber} added to slide ${slide.index + 1}`)

//       // Update WebSocket
//       if (ws.readyState === WebSocket.OPEN) {
//         ws.send(`${slide.index}|${JSON.stringify(myData)}`)
//       }
//     })
//   } catch (error) {
//     console.error('Error adding data to selected slide:', error)
//   }
// }

// const initWS = () => {
//   ws = new WebSocket('ws://127.0.0.1:17890')

//   ws.onopen = () => {
//     console.log('WebSocket connected')
//   }

//   ws.onclose = () => {
//     console.log('WebSocket disconnected')
//     setTimeout(initWS, 1000)
//   }

//   ws.onerror = (err) => {
//     console.error('WebSocket error:', err)
//   }

//   ws.onmessage = (event) => {
//     console.log('Received:', event.data)
//     const [slideIndex, dataSlide] = event.data.split('|')
//     console.log('slideIndex, dataSlide', slideIndex, dataSlide)
//     const data = JSON.parse(dataSlide)
//     EventBus.$emit('ppt-active-slide-from-socket', data.value)
//   }
// }

// The ONE place that commits the Office platform into the store. Everything platform-
// dependent reads it (auto-start detection, SlideWrapper's platform checks, the
// platform-variant feature flags, the PowerpointOnlineNotSupport banner), so it must run
// on EVERY add-in load — no matter which branch of handleDataAfterLoadOffice ends up
// resolving the frame. Idempotent: safe to call more than once per load.
const captureOfficePlatform = () => {
  if (!window.Office) return
  if (!window.Office.context) return
  if (!window.Office.context.host) return
  if (window.Office.context.platform === officeConstants.OFFICE_ONLINE) {
    store.commit('setIsOfficeOnline', true)
  }
  store.commit('setAddinPPTPlatform', window.Office.context.platform)
}

const loadDataFromSlidePowerPoint = async (key) => {
  if (!window.Office) return null
  if (!window.Office.context) return null
  if (!window.Office.context.host) return null
  captureOfficePlatform()
  return new Promise((resolve, reject) => {
    window.Office.context.document.settings.refreshAsync((asyncResult) => {
      if (asyncResult.status === window.Office.AsyncResultStatus.Failed) {
        reject()
      } else {
        const ahaSlideId = window.Office.context.document.settings.get(key)
        if (ahaSlideId !== null) {
          resolve(ahaSlideId)
        } else {
          resolve()
        }
      }
    })
  })
}

const setDataSlideInPowerpointToState = (ahaPresentationId, ahaSlideId) => {
  store.state.powerPointPresentationId = ahaPresentationId
  store.state.powerPointSlideId = ahaSlideId

  // if (ahaSlideId) {
  //   addDataToSelectedSlide(ahaSlideId)
  // }
}

// AHA-46329: unbind this frame from its host PowerPoint slide. The editor's back path
// already clears document.settings and the AHA-46192 last-selected presentation tag, but
// the slide tag outranks both on load — leaving it behind sends the user straight back
// into the presentation they just left.
//
// AHA-46751: no longer gated on canUsePptSlideSelector either. That gate's premise — "flag off =>
// no tag was ever written" — stopped holding once mirrorDataSlideBinding began writing the tag on
// EVERY dataSlidePPT write (see its comment): with 46329 off a tag did exist and this skipped
// clearing it. That was inert only for as long as the load path did not read it back, which
// openSlideFromHostSlideTag above no longer promises — so leaving the gate here would turn the
// widened read into exactly the back-trap this function exists to prevent. Nothing is lost by
// dropping it: clearSlideBindingFromHostSlide already no-ops on a host without the tag API.
const clearSlideBindingForCurrentFrame = async () => {
  await clearSlideBindingFromHostSlide()
}

const setDataPresentationInPowerpointToState = (ahaPresentationId) => {
  store.state.activePresentationPowerpointId = ahaPresentationId
}

// `persist` defaults to true so the legacy (flag-off) caller keeps its exact original
// behaviour — a remap that MOVES the binding writes it back into document.settings. The AHA-46329
// tag branch passes { persist: false }: on Office web, settings are SHARED across add-in
// frames, so a cloned frame that stamped its own binding there would overwrite (and thus
// corrupt) the binding every other clone reads. The tag on the frame's own host slide is
// already the source of truth for those frames — nothing needs mirroring.
const handleGetCorrentSlideIdByMultipleSession = async (
  presentationId,
  slideId,
  { persist = true, previousSlideIds } = {},
) => {
  // Logged BEFORE the request as well as after it: a request that never comes back looks exactly
  // like one that was never made, and this is the only place a binding's history can be written.
  logRemap('asking | presentation', presentationId, '| slide', slideId, '| persist =', persist)
  try {
    const resp = await slideApi.getCurrentSlideIdByOldSlideId({ presentationId, slideId })
    logRemap('answer for slide', slideId, '| slideMap =', resp && resp.slideMap)
    if (resp && resp.slideMap && resp.slideMap[slideId]) {
      const remappedSlideId = resp.slideMap[slideId]
      const payload = {
        presentationId,
        slideId: remappedSlideId,
      }
      // AHA-46751: write ONLY when the remap actually moved the binding. The overwhelmingly
      // common answer is "same slide", and that write was never free — it drags the host-slide
      // tag mirror along with it, and the mirror resolves its target by DOCUMENT-global
      // selection. This runs on cold load, which includes the reload PowerPoint gives every
      // content add-in frame when it enters the Slide Show: the frame is then re-stamping
      // whatever slide is on SCREEN with its own binding, clobbering that slide's tag and
      // making its real owner see the shown slide as somebody else's. Writing identical data
      // back into settings bought nothing to pay for that.
      //
      // Compared as strings: the remap API answers with numbers while a binding that came from
      // a tag is strings, and a shape mismatch here would read as "changed" and reinstate the
      // write on every single load.
      //
      // And when it DOES move, the settings write goes out WITHOUT the tag mirror. Narrowing the
      // write shrinks this vector but does not close it: writeSlideBindingToHostSlide resolves its
      // target with the same DOCUMENT-global getSelectedDataAsync, so on the Slide-Show entry
      // reload the rarer moved-id branch would still stamp this frame's binding onto whatever
      // slide is on SCREEN. The read-view guard is the only thing in front of it, and a guard is a
      // weaker answer than not needing one. A cold load has no selection it can trust; it also
      // does not need the tag write — the remap is idempotent and re-runs on every load, and the
      // web tag branch (openSlideFromHostSlideTag) already passes { persist: false } and never
      // mirrors. document.settings — the actual point of `persist` — is unaffected:
      // saveDataToSlidePowerPoint writes settings regardless of mirrorToHostSlide.
      const moved = String(remappedSlideId) !== String(slideId)
      if (!moved) {
        logRemap('slide', slideId, 'did not move -> nothing recorded, settings untouched')
      } else if (!persist) {
        // The three { persist: false } call sites are the web / read-view paths, which write no
        // settings at all — and the history is only ever READ by the desktop-only ownership
        // check, so not recording there costs nothing today. Logged anyway: if a binding ever
        // looks history-less on a host that DID remap, this line says the frame took this path.
        logRemap('slide', slideId, '->', remappedSlideId, '| persist = false -> no history written (web/read-view path)')
      }
      if (persist && moved) {
        // AHA-46751: the binding remembers what it used to point at. From here on
        // document.settings hold the NEW id while the binding tag on the host slide still holds
        // the old one, and the auto-start ownership check compares exactly those two — this
        // history is what lets it see through the reset (services/office/current-slide).
        //
        // It rides along on the settings write that was happening anyway, and it is the ONLY
        // write allowed to carry one — saveDataToSlidePowerPoint strips it from every other, so
        // a rebind cannot leave a history behind that describes the binding it replaced.
        payload.previousSlideIds = appendPreviousSlideId(previousSlideIds, slideId)
        logRemap('slide', slideId, '->', remappedSlideId, '| history now', payload.previousSlideIds)
        // The tag is repaired from the read view instead (healStaleTagBinding), where ownership
        // has been positively confirmed for the slide being written — which is exactly what a
        // cold load cannot do. Unconditional, not gated on canUsePptSlideSelector: with the tag
        // stack off there is no heal to fall back on, but there is also nothing that needs one.
        // The remap re-runs on every load and answers old -> new for as long as the tag holds the
        // old id, so a tag left un-mirrored still resolves; keeping the write to preserve
        // flag-off behaviour would only preserve the corruption.
        await saveDataToSlidePowerPoint('dataSlidePPT', payload, {
          mirrorToHostSlide: false,
          // The one write that is allowed to carry a history: this is not a new binding, it is
          // the SAME one following its slide through a server-side reset.
          keepSlideIdHistory: true,
        })
      }
      return payload
    }
    // No entry for this slide at all — distinct from "answered the same id", and the only path
    // that returns undefined. The { persist: false } callers absorb that with `|| binding`; the
    // legacy settings caller does not, so if this line prints, look for a TypeError right after it.
    logRemap('no slideMap entry for slide', slideId, '-> no remap, returning undefined')
  } catch (error) {
    logRemap('remap request FAILED for presentation', presentationId, '| slide', slideId, '|', error)
    return { presentationId, slideId }
  }
}

// AHA-46192: fall back to the last presentation selected in THIS document when a
// frame loads with no selection of its own. This is its OWN branch, not data
// substitution: the tag holds only a presentationId, so we just open that
// presentation's detail page — NOT present mode (no presenting/ppt-presentation
// query, no setDataPresentationInPowerpointToState), so it is not treated as a
// selected/presenting presentation. Flag-gated; handleOnLoadOffice awaits
// UnleashService.ready before calling, so the getter is populated. Flag off, tag
// absent, or PowerPointApi 1.3 unavailable => nothing opened (behaviour unchanged).
const openLastSelectedPresentationFallback = async () => {
  // Bail before the tag round-trip on auth pages / when the flag is off.
  if (!isPptDefaultPresentationEnabled() || isAuthPage()) {
    logRoute(
      'fallback skipped | defaultPresentationFlag =', isPptDefaultPresentationEnabled(),
      '| authPage =', isAuthPage(),
      '-> staying on default route (presentation list)',
    )
    return
  }
  const presentationId = await loadLastSelectedPresentation()
  if (!presentationId) {
    // THE bug's silent path: no source resolved a binding, so nothing navigates and the
    // app lands on its default route — the presentation list. If this line is what prints
    // on a web Slide Show cold-load, the slide-show session has lost every binding source.
    logRoute('fallback: last-selected tag empty -> staying on default route (presentation list)')
    return
  }
  if (window.location.href.indexOf('/presentation/') === -1) {
    // AHA-46329: a blank frame is a freshly-inserted add-in with no binding of its own,
    // so when the multi-select flow is available the slide selector — not the editor — is
    // where it should land. 46329 off (or no PowerPointApi 1.8) => the original editor route.
    if (canUsePptSlideSelector()) {
      logDefault('no local selection -> opening slide selector for', presentationId)
      replaceRoute({
        name: ROUTE_NAMES.PPT_SLIDE_SELECTOR,
        params: { presentationId },
      })
      return
    }
    logDefault('no local selection -> opening last-selected presentation', presentationId)
    replaceRoute({
      name: ROUTE_NAMES.PRESENTATION_DETAIL,
      params: { id: presentationId },
    })
  }
}

// AHA-43111: navigate this frame into present mode on a resolved slide.
//
// A plain route change on every platform. Carrying the slide identity in the URL was tried and
// abandoned: Office reloads the manifest SourceLocation when the deck enters Slide Show, so the
// runtime URL — and any ?slideId= on it — is discarded before the new session starts (confirmed).
// The Slide Show session resolves its slide from the localStorage bridge instead
// (see openPptOnlineBindingFallback), which is why this takes no slideId.
const navigateToPresentingSlide = ({ presentationId }) => {
  // Route-only: no binding write here. Both callers have ALREADY persisted this binding through the
  // right per-slide stores — the selector via its insert loop (plus its own mirrorToHostSlide:false
  // settings/doc-URL/last-selected write with the identical value), openSlideFromHostSlideTag from
  // the host-slide tag it just resolved. The old saveDataToSlidePowerPoint here was pure
  // duplication on the selector path, and on the web tag path its document.settings write pushed a
  // cloned frame's binding into the frame-SHARED settings that
  // handleGetCorrentSlideIdByMultipleSession({ persist: false }) deliberately leaves untouched.
  replaceRoute({
    name: ROUTE_NAMES.PRESENTATION_DETAIL,
    params: { id: presentationId },
    query: { presenting: true },
  })
}

// AHA-46329: a frame that was created by CLONING the add-in's slide inherits the donor's
// document.settings, so settings cannot tell the clones apart. The inserting frame tags
// each PowerPoint slide it creates with its AhaSlides {presentationId, slideId}, and each
// frame resolves its own binding from the tags on the slide it lives on. This runs BEFORE
// the settings branch so the tag always wins for a cloned frame. Returns true when it
// handled the load.
//
// AHA-46751: gated on canReadSlideTags(), NOT on canUsePptSlideSelector(). This branch is
// per-frame IDENTITY, not the multi-select feature — and a deck reaches several add-in frames
// without the selector ever running (Insert > Add-ins twice, or copy/pasting the slide). On web
// document.settings are SHARED across frames, so this tag read is the only thing that can hand a
// frame a binding of its own; with the selector off every web frame fell through to settings and
// resolved the LAST writer's binding. The auto-start ownership check then compared the correct
// shown slide against the wrong owner, so either nobody started (a dead slide) or everybody did
// (the oscillation this ticket exists to stop) — and auto-start is gated on the 43226 flag, which
// says nothing about 46329. canReadSlideTags() is the capability this branch actually depends on,
// and it is the same one canUseAddinPowerpointAutoStart requires, so the two can no longer
// disagree. Desktop is unaffected either way: it returns early just below.
const openSlideFromHostSlideTag = async () => {
  if (!canReadSlideTags() || isAuthPage()) {
    return false
  }
  // AHA-46329 (desktop): this branch answers "which slide am I on?" with
  // getSelectedDataAsync, which is DOCUMENT-GLOBAL — every frame gets the user's current
  // selection, so cloned frames booting at staggered times all read some other slide's tags.
  // That is what duplicated and dropped slides on a multi-slide Add. On desktop each clone
  // now carries its own document.settings snapshot, baked in at export time
  // (see runDesktopInsert), so the settings branch below resolves it correctly and this
  // branch can only get in the way. The web still needs it: settings are shared live across
  // frames there, leaving tags as the only thing that can tell clones apart.
  if (isDesktopOfficeHost()) {
    log('desktop -> skipping host-slide tag branch, resolving from baked settings')
    return false
  }
  const binding = await readSlideBindingFromHostSlide()
  if (!binding) {
    // On web this is the ONLY per-frame signal; an empty read here on a Slide Show cold-load
    // means the slide-show session can't see the host-slide tag, pushing routing down to
    // document.settings and then the last-selected fallback.
    logRoute('host-slide tag: no binding on this slide')
    return false
  }
  logRoute('host-slide tag: binding =', binding)
  // AHA-43111 read-view bridge, refreshed on every edit-view cold load. readTagsBySlideToken
  // found THIS frame's slide by its window.name token and handed back that slide's Common-API
  // id along with the tags, so this stamps the exact pair the Slide Show will look up — with no
  // selection read and no dependence on how the binding got onto the slide. That makes the
  // bridge self-healing: a rebind that never went through the insert path (ordinary editor
  // buttons) lands here on the next load and corrects a stale entry. The RAW tag binding is
  // stored, not the remapped `data` below — the read side runs its own session remap.
  savePptCommonSlideBinding(binding.commonSlideId, binding)
  // The helper resolves to undefined when the remap API answers without a matching entry,
  // so absorb that here and keep the tag's own binding. { persist: false }: never mirror a
  // clone's binding into the frame-shared document.settings (see the helper's comment).
  const data = (await handleGetCorrentSlideIdByMultipleSession(
    binding.presentationId,
    binding.slideId,
    { persist: false },
  )) || binding
  // MUST be numeric — tag values are strings, and the store binding is compared with a
  // strict === against a numeric id downstream. See toNumericId.
  setDataSlideInPowerpointToState(toNumericId(data.presentationId), toNumericId(data.slideId))
  // Only when this frame isn't already on a presentation URL. Web used to be exempted from that
  // check so it would always re-navigate — that only made sense while navigateToPresentingSlide
  // did a full reload onto a ?slideId= URL; now that it is a plain router.replace, exempting web
  // just replaces the current route with itself (a NavigationDuplicated rejection) on every
  // cold-load that starts from an already-resolved presentation.
  if (window.location.href.indexOf('/presentation/') === -1) {
    navigateToPresentingSlide({ presentationId: data.presentationId })
  }
  return true
}

// Looks the Common-API slide id up in the per-slide bridge and, if it resolves, points this frame
// at that AhaSlides slide. Extracted from the fallback below because it now has two drivers: this
// frame's own getSelectedDataAsync, and a slide id broadcast by whichever frame the read session
// chose to serve. Returns true when it resolved and acted.
const routeToCommonSlide = async (commonSlideId) => {
  const binding = loadPptCommonSlideBinding(commonSlideId)
  if (!binding || !binding.presentationId) {
    logRoute('read view: no common-slide binding for id', commonSlideId, '-> falling through')
    return false
  }
  logRoute('resolved -> common-slide bridge | id', commonSlideId, '|', binding.presentationId, binding.slideId)
  const remapped = (await handleGetCorrentSlideIdByMultipleSession(
    binding.presentationId,
    binding.slideId,
    { persist: false },
  )) || binding
  // AHA-46751 (web): fold the remap back into the bridge. The entry was written in the EDIT session
  // and holds pre-reset ids; the store below holds post-reset ones, and the auto-start ownership
  // check compares exactly those two (see THE WEB PATH in current-slide). Without this a
  // present-run reset would have every frame read "the shown slide is somebody else's" and stop
  // starting — the same divergence the desktop path repairs with healTagsAtSlideIfUnchanged.
  //
  // Safe to make the bridge authoritative: the remap API answers "same slide" for an id that is
  // already current, so a later cold load re-remapping this value is a no-op.
  if (String(remapped.slideId) !== String(binding.slideId)) {
    logRoute('bridge entry was pre-remap -> refreshing | id', commonSlideId,
      '|', binding.slideId, '->', remapped.slideId)
    savePptCommonSlideBinding(commonSlideId, remapped)
  }
  setDataSlideInPowerpointToState(toNumericId(remapped.presentationId), toNumericId(remapped.slideId))
  if (!isAuthPage() && window.location.href.indexOf('/presentation/') === -1) {
    replaceRoute({
      name: ROUTE_NAMES.PRESENTATION_DETAIL,
      params: { id: binding.presentationId },
      query: { presenting: true },
    })
    logRoute('routed -> PresentationDetail', binding.presentationId, '| slide', remapped.slideId)
  } else {
    logRoute('already on a presentation URL -> no route change | slide', remapped.slideId)
  }
  return true
}

// Last resort in the read view: the ONE document-wide binding the edit session mirrored into
// localStorage. It cannot tell the slides of a multi-slide deck apart — that is exactly what the
// common-slide bridge above exists for — but a deck bound to a single presentation resolves
// correctly from it, and opening the right presentation beats stranding the frame on the
// presentation list. Safe to try last precisely because it is last: it only ever runs when the
// per-slide sources produced nothing. The broadcast listener stays armed either way, so when the
// served frame publishes, routeToCommonSlide corrects this frame to the exact slide.
const routeToDocumentBinding = async () => {
  const binding = loadPptOnlineBinding()
  if (!binding || !binding.presentationId) return false
  if (isAuthPage()) {
    logRoute('document binding available but on an auth page -> not routing')
    return false
  }

  if (!binding.slideId) {
    logRoute('resolved -> document binding (whole presentation)', binding.presentationId)
    setDataPresentationInPowerpointToState(binding.presentationId)
  } else {
    logRoute('resolved -> document binding (single slide)', binding.presentationId, binding.slideId)
    const remapped = (await handleGetCorrentSlideIdByMultipleSession(
      binding.presentationId,
      binding.slideId,
      { persist: false },
    )) || binding
    setDataSlideInPowerpointToState(toNumericId(remapped.presentationId), toNumericId(remapped.slideId))
  }

  if (window.location.href.indexOf('/presentation/') === -1) {
    replaceRoute({
      name: ROUTE_NAMES.PRESENTATION_DETAIL,
      params: { id: binding.presentationId },
      query: { presenting: true },
    })
  }
  return true
}

// What a broadcast delivery DOES. The channel itself (arm-once, publish, disarm) lives in
// ppt-online-current-slide; routing is this module's concern, so it stays here and is handed over.
// The returned promise is the channel's disarm signal — false (an id with no stored binding) keeps
// the frame subscribed for the next publish, which is why the failure path resolves false rather
// than swallowing the rejection.
const routeOnBroadcast = () => followBroadcastCurrentSlide((commonSlideId) => {
  return routeToCommonSlide(commonSlideId).catch((error) => {
    logRoute('broadcast route failed', error)
    return false
  })
})

const openPptOnlineBindingFallback = async () => {
  // Web only. Every store and channel this path touches is already a no-op off PowerPoint
  // Online, so this gate is not what makes it safe — it is what stops the frame paying for it:
  // the active-view query and the bounded getSelectedDataAsync below are real host round trips
  // on the cold-load critical path, issued to answer a question that can only ever resolve on
  // web. Off web there is nothing here that can resolve anything.
  if (!isPptOnlineAddin()) {
    return false
  }

  // Only resolve in the Slide Show (read) view. In edit view a multi-insert creates several frames
  // that must each resolve their OWN slide from the window.name-token host-slide tag; the active
  // slide there is document-global and would pin every frame to the same slide.
  const view = await queryActiveView()
  if (view !== 'read') {
    logRoute('not slideshow (view =', view, ') -> skipping active-slide resolve')
    return false
  }

  // Read mode: window.name has no slide token, document.settings comes up empty, the Rich API
  // throws, and the URL is the shared manifest SourceLocation — none carry per-slide identity
  // into the Slide Show session. But the show displays ONE slide at a time and
  // getSelectedDataAsync(SlideRange) tracks it (Common API, works in read), so we read the current
  // slide's Common-API id and look its binding up in the per-slide localStorage bridge that the
  // edit session wrote (keyed by the SAME id — see savePptCommonSlideBinding).
  //
  // Bounded, because only one frame per document ever gets an answer here: an unbounded await would
  // never settle in the others, and everything sequenced after it — the remaining cold-load
  // fallbacks included — would be unreachable in exactly the frames that need them. A frame that
  // times out subscribes to the winner's broadcast instead of asking again.
  //
  // A late answer is still worth having: the timeout settles our promise but cannot cancel the
  // Office call, so if the host does eventually reply, publish it rather than discard it.
  let commonSlideId = null
  try {
    commonSlideId = await readSelectedSlideIdBounded(SELECTED_SLIDE_READ_TIMEOUT_MS, (lateId) => {
      if (!lateId) return
      logRoute('getSelectedDataAsync answered LATE ->', lateId, '| publishing')
      publishShowCurrentSlideId(lateId)
      routeToCommonSlide(lateId).catch(() => {})
    })
  } catch (error) {
    logRoute('own getSelectedDataAsync unavailable |', error && error.message)
  }

  if (commonSlideId) {
    // This frame is the one the read session serves, so its answer is the only copy of "which
    // slide is on screen" in the document: publish it for the starved frames first, route second.
    // Published once, not on a timer — see the SCOPE note in ppt-online-current-slide.
    publishShowCurrentSlideId(commonSlideId)
  } else {
    // Starved. The winner may have published before this frame armed its listener, so check the
    // stored value as well as subscribing.
    routeOnBroadcast()
    commonSlideId = readShowCurrentSlideId()
    if (!commonSlideId) {
      logRoute('no own answer and nothing broadcast yet -> document binding, listener armed')
      return routeToDocumentBinding()
    }
  }

  // A per-slide answer that has no binding stored against it is still a miss, so it drops to the
  // same document-wide last resort as having no answer at all.
  return (await routeToCommonSlide(commonSlideId)) || routeToDocumentBinding()
}

// The cold-load fallback chain, tried when a frame loads with no host-slide tag and no
// document.settings selection of its own. Order matters:
//   1. AHA-43111 common-slide bridge — the ONLY per-slide source in a PowerPoint Online Slide Show
//      session (Rich API throws and settings are empty there). Resolves from this frame's own
//      getSelectedDataAsync, or from the slide id broadcast by whichever frame the read session
//      served. No-ops to false outside the read view, and in the read view when neither is
//      available yet — in which case it leaves a broadcast listener armed on the way out.
//   2. AHA-46192 last-selected presentation tag — the desktop/edit-view fallback (needs the Rich
//      API, so it can only succeed off the web read view).
const openColdLoadFallbacks = async () => {
  if (await openPptOnlineBindingFallback()) {
    return
  }
  logRoute('no frame-local binding (host-slide tag + settings both empty) -> last-selected fallback')
  await openLastSelectedPresentationFallback()
}

const handleDataAfterLoadOffice = async () => {
  // Must happen before any branch can return: loadDataFromSlidePowerPoint (below) used to be
  // the only place platform state was committed, so an early return from the tag branch left
  // addinPPTPlatform null on exactly the frames AHA-46329 creates.
  captureOfficePlatform()

  // AHA-43111 diagnostic: the single most useful line for the "presenting shows the
  // presentation list on web" bug — it pins the platform/view/URL this cold-load started
  // from, so a slide-show reload (brand-new web session, non-/presentation/ URL) is visible.
  logRoute(
    'start | platform =', window.Office?.context?.platform,
    '| view =', store.state.activeViewMode,
    '| onPresentationUrl =', window.location.href.indexOf('/presentation/') !== -1,
    '| authPage =', isAuthPage(),
    '| href =', window.location.href,
  )

  // AHA-43111: a signed-out web add-in must go to the device grant, not the in-frame
  // login form. Return early — there is no selection to restore while signed out.
  if (redirectWebAddinToDeviceGrant()) {
    logRoute('resolved -> device grant (signed-out web add-in)')
    return
  }

  if (await openSlideFromHostSlideTag()) {
    logRoute('resolved -> host-slide tag (PowerPoint.run)')
    return
  }

  let dataSlidePPT = await loadDataFromSlidePowerPoint('dataSlidePPT')
  logRoute('document.settings dataSlidePPT =', dataSlidePPT)

  if (dataSlidePPT && dataSlidePPT.activePresentationId) {
    logRoute('resolved -> settings (whole presentation)', dataSlidePPT.activePresentationId)
    setDataPresentationInPowerpointToState(dataSlidePPT.activePresentationId)
    if (isAuthPage()) {
      return
    }
    if (window.location.href.indexOf('/presentation/') === -1) {
      replaceRoute({
        name: 'PresentationDetail',
        params: { id: dataSlidePPT.activePresentationId },
        query: { presenting: true, 'ppt-presentation': true },
      })
    }
    return
  }
  if (dataSlidePPT && dataSlidePPT.slideId && dataSlidePPT.presentationId) {
    logRoute('resolved -> settings (single slide)', dataSlidePPT.presentationId, dataSlidePPT.slideId)
    // The binding's existing history goes in so a SECOND reset appends to it rather than
    // replacing it: after A->B->C a tag still holding A must stay recognisable.
    dataSlidePPT = await handleGetCorrentSlideIdByMultipleSession(
      dataSlidePPT.presentationId,
      dataSlidePPT.slideId,
      { previousSlideIds: dataSlidePPT.previousSlideIds },
    )
    setDataSlideInPowerpointToState(dataSlidePPT.presentationId, dataSlidePPT.slideId)
    if (window.location.href.indexOf('/presentation/') === -1) {
      if (isAuthPage()) {
        return
      }
      replaceRoute({
        name: 'PresentationDetail',
        params: { id: dataSlidePPT.presentationId },
        query: { presenting: true },
      })
    }
    return
  }
  // Neither the host-slide tag nor document.settings resolved this frame -> read-view fallbacks.
  await openColdLoadFallbacks()
}

// AHA-43111: the add-in's landing route is a WAITING ROOM, never a destination.
//
// A frame boots at the manifest SourceLocation with no presentation id on the URL, so the
// router parks it on a neutral spinner (router.js defaultUrl) instead of rendering the
// presentation list it would usually be replacing a moment later. Every branch above that
// resolves a binding navigates on its own; this is the other half of the contract — when the
// cold load ends with the frame still sitting on the landing route, it means no binding source
// answered (or the chain threw), and the presentation list is exactly where the add-in used to
// land in that case. Restoring that is the whole job here.
//
// Two conditions, and each is only trustworthy because the other one holds:
//  - nothing navigated, so router.currentRoute has not gone stale and still names the route
//    this frame actually booted on (see coldLoadNavigated for why it goes stale otherwise);
//  - that route is the landing route, so the frame really is one that is waiting on us. A frame
//    the guard sent to /pages/oauth, or one that cold-loaded straight onto /presentation/,
//    is not, and must be left alone.
const settleAddinLandingRoute = () => {
  if (coldLoadNavigated) {
    return
  }
  if (router.currentRoute?.name !== ROUTE_NAMES.PPT_ADDIN_LANDING) {
    return
  }
  logRoute('cold load ended with no binding -> presentation list')
  const navigation = router.replace({ name: ROUTE_NAMES.PRESENTATIONS })
  if (navigation && typeof navigation.catch === 'function') {
    navigation.catch((error) => {
      if (error && error.name === 'NavigationDuplicated') return
      Sentry.captureException(error)
    })
  }
}

const reCheckActiveView = () => {
  window.Office?.context?.document?.getActiveViewAsync((result) => {
    store.commit('setActiveViewMode', result.value)
  })
}

const isAddinAutoStartEnabled = () => {
  const enableAddin = store.state.isEnableAddinPowerpoint
  const storeGetterFlag = store.getters['featureToggles/canUseAddinPowerpointAutoStart']
  const platform = store.state.addinPPTPlatform

  // Windows desktop: the original path. The active-view + ResizeObserver + slide-focus-poll
  // flow relies on the ActiveViewChanged 'edit' signal to stop polling; that signal is flaky
  // on Mac (office-js #102), so Mac stays excluded and falls back to the manual-start modal.
  const isWindowsPlatform = platform === officeConstants.PC

  // AHA-43111 — PowerPoint on the web. ActiveViewChanged never fires here either, but unlike
  // Mac that costs us nothing: web treats Slide Show as a brand-new session, so the add-in
  // cold-loads already in 'read' view and setupAutoStartViewMachine's init-query is the
  // correct primary signal. Ending the show tears the session down, taking the poll with it.
  const isWebPlatform = isPptOnlineAddin()

  const enabled = Boolean(enableAddin && storeGetterFlag && (isWindowsPlatform || isWebPlatform))
  log('autoStartEnabled =', enabled, '| flag =', storeGetterFlag, '| platform =', platform,
    '| enableAddin =', enableAddin)
  // canUseAddinPowerpointAutoStart is three ANDs and the line above shows only their product, so
  // a false could not be told apart: the Unleash flag never arriving (its fetch is raced against
  // UNLEASH_READY_TIMEOUT_MS during add-in load, and a timeout leaves the mirror empty), the
  // platform not yet committed, or a host with no slide tags at all. Completely different fixes.
  //
  // The flag alone cannot tell the first case from the third: the frontend API sends only
  // ENABLED toggles, so an off flag is simply absent from the mirror and reads exactly like one
  // that never arrived. (Printed as a boolean for that reason — the getter's raw `undefined` is
  // the normal representation of "off", not evidence of anything.) The mirrored count is what
  // separates them: 0 = nothing fetched at all (raise UNLEASH_READY_TIMEOUT_MS / check the
  // proxy), non-zero = the fetch worked and this flag is off for this context in the
  // `development` Unleash environment — turn it on there, or use
  // ?unleashOverride=AHA-43226-ppt-auto-start:on on the manifest SourceLocation.
  if (!storeGetterFlag) {
    log('autoStart flag = false because | unleash AHA-43226-ppt-auto-start =',
      // Optional call, not a plain one: this getter is a FUNCTION getter (it takes the flag
      // name), unlike every other getter read here, so an unregistered featureToggles module
      // makes it undefined rather than falsy. A diagnostic must never be the thing that throws
      // — this runs inside detectActiveViewChange, on the add-in's critical load path.
      Boolean(store.getters['featureToggles/isFeatureEnabled']?.('AHA-43226-ppt-auto-start')),
      '| flags mirrored =', Object.keys(store.state.featureToggles?.features || {}).length,
      '| addinPPTPlatform =', platform,
      '| canReadSlideTags =', canReadSlideTags())
  }
  return enabled
}

const getActiveViewValue = () => new Promise((resolve, reject) => {
  const doc = window.Office?.context?.document
  if (!doc) {
    // Office not ready yet — not an error; callers skip a falsy view.
    resolve(undefined)
    return
  }
  doc.getActiveViewAsync((result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    if (failed) {
      // A real API failure rejects so the state machine's fail-safe is reachable.
      reject(result?.error || new Error('getActiveViewAsync failed'))
      return
    }
    resolve(result?.value)
  })
})

const resyncActiveView = async (source = 'resync') => {
  try {
    const view = await getActiveViewValue()
    log('resync', source, '-> view =', view)
    if (view && viewMachine) {
      lastViewSource = source
      viewMachine.handleView(view)
    }
  } catch (error) {
    // Init/resize re-sync is best-effort; swallow to avoid unhandled rejections.
    log('resync', source, 'error:', error)
  }
}

// The state machine's onEnterRead. Named rather than inlined so setupAutoStartViewMachine stays
// readable as the wiring it is.
//
// AHA-46751: one path for every frame, and nothing is captured on it. The old path recorded the
// DOCUMENT-GLOBAL shown slide as "mine" — the false claim that had every add-in frame in the deck
// fighting over the audience — so it is gone rather than kept behind the flag. The tag poll asks
// the slide what it is bound to instead, which needs no captured id.
const handleEnterReadView = () => {
  log('read', lastViewSource)
  // The poll reports back through these rather than reaching for the store itself — see the
  // comment on startTagSlideFocusPolling for why that module has to stay a leaf.
  startTagSlideFocusPolling({
    onMine: () => store.dispatch(AUTO_START_ACTION),
    onOther: () => store.commit('setPptSlideLostFocus', true),
  })
  // Confirm before starting. Async, so the start now trails the view change by a tag read
  // (bounded, desktop only) instead of firing in the same tick — the cost of not having every
  // frame in the deck start at once.
  isActiveSlideBoundToThisFrame()
    .then((isMine) => {
      if (!isMine) {
        log('shown slide belongs to another frame -> not auto-starting')
        return null
      }
      store.dispatch(AUTO_START_ACTION)
      return null
    })
    // Fail-open is the contract, so an unexpected throw must still start rather than leave the
    // presenter with nothing.
    .catch((error) => {
      log('confirm threw -> starting anyway', error)
      store.dispatch(AUTO_START_ACTION)
    })
}

const setupAutoStartViewMachine = () => {
  const officeDoc = window.Office?.context?.document
  const activeViewChanged = window.Office?.EventType?.ActiveViewChanged

  // Idempotent: if a prior machine/observer/handler/poll exists (e.g.
  // detectActiveViewChange runs again), tear them down first so we don't leak a
  // ResizeObserver, a duplicate ActiveViewChanged handler, or a stray poll timer.
  if (viewMachine) viewMachine.dispose()
  if (viewResizeObserver) {
    viewResizeObserver.disconnect()
    viewResizeObserver = null
  }
  if (officeDoc && activeViewChanged && activeViewHandler) {
    officeDoc.removeHandlerAsync(activeViewChanged, { handler: activeViewHandler })
    activeViewHandler = null
  }
  stopTagSlideFocusPolling()

  // AHA-46751 (web): how the ownership check learns which AhaSlides slide THIS frame resolved to.
  // Handed in rather than imported there, because current-slide is a leaf and @/vuex/store's
  // module graph reaches back into services/office — see startTagSlideFocusPolling's comment.
  registerOwnBindingReader(() => ({
    presentationId: store.state.powerPointPresentationId,
    slideId: store.state.powerPointSlideId,
  }))

  viewMachine = createPptActiveViewMachine({
    getActiveView: getActiveViewValue,
    commitView: (view) => {
      store.commit('setActiveViewMode', view)
      // The ONE place the poll stops, on every platform: a COMMITTED 'edit' — meaning the
      // machine's 200ms debounce elapsed and a fresh getActiveViewAsync still said 'edit'
      // (or failed). On web that is the only possible path, since ActiveViewChanged never
      // fires there.
      //
      // Windows used to short-circuit this from the raw ActiveViewChanged handler, which is
      // wrong: starting a Slide Show does not close the editing window, so clicking back into
      // it fires 'edit' while the show is still running, and one such click killed the frame's
      // slide tracking for the rest of the show. The event is a prompt to ASK the host, never
      // the answer — the same rule the rest of this module already follows.
      //
      // The gate this replaces was justified by that short-circuit: pptActiveViewMachine also
      // commits 'edit' from its catch branch when the re-query rejects, and adding that path on
      // Windows was a regression while the handler was already stopping the poll unconditionally.
      // With the short-circuit gone the comparison inverts — stopping now needs the event PLUS a
      // re-query that confirms or fails, where before the event alone was enough.
      if (view === 'edit') stopTagSlideFocusPolling()
    },
    onEnterRead: handleEnterReadView,
  })

  // Both directions go to the machine. 'read' used to be dropped here, because it fires for
  // EVERY add-in frame in the deck on "Play from Start" (Office issue #102) and onEnterRead
  // meant "this frame starts presenting" — so honouring it had every frame claim the audience.
  //
  // That is no longer what onEnterRead means. It runs isActiveSlideBoundToThisFrame() first and
  // dispatches only for the frame the shown slide's tag actually names (AHA-46751), so the
  // non-focused frames answer OTHER and stand down. The event cannot single out a frame — but it
  // no longer has to, because nothing downstream trusts it to.
  //
  // What the drop was costing: after a sustained click into the editing window, 'edit' commits
  // and the poll stops. Clicking back into the running show fires 'read' and nothing else does —
  // no reload, no resize — so the frame stayed written off for the rest of the show. This is the
  // one signal that recovers it.
  //
  // The extra cost is bounded: honouring 'read' means one ownership tag batch per frame per
  // event, which is the same batch every frame already runs from its init-query on show entry,
  // and resolveActiveSlideOwnership is single-flighted so a burst within one frame collapses.
  if (officeDoc && activeViewChanged) {
    activeViewHandler = (args) => {
      log('view ->', args.activeView, '| confirming before acting')
      lastViewSource = 'ActiveViewChanged'
      // A prompt, never a verdict: 'edit' is committed only if the machine's re-query backs it
      // up, and 'read' reaches auto-start only through the ownership check.
      viewMachine.handleView(args.activeView)
    }
    officeDoc.addHandlerAsync(activeViewChanged, activeViewHandler)
  }

  // Resize re-sync: catches view changes the event misses (Mac "play from current",
  // slideshow window resize). Guarded for environments without ResizeObserver.
  if (typeof ResizeObserver !== 'undefined') {
    viewResizeObserver = new ResizeObserver(() => {
      resyncActiveView('ResizeObserver')
    })
    viewResizeObserver.observe(document.documentElement)
  } else {
    log('no ResizeObserver')
  }

  // Init query: covers web (ActiveViewChanged never fires there) and cold-start
  // into a running show.
  resyncActiveView('init-query')
}

const detectActiveViewChange = () => {
  if (isAddinAutoStartEnabled()) {
    log('detect -> auto-start path')
    setupAutoStartViewMachine()
    return
  }
  log('detect -> legacy path')
  window.Office?.context?.document?.addHandlerAsync(
    window.Office.EventType.ActiveViewChanged,
    (args) => {
      store.commit('setActiveViewMode', args.activeView)
    },
  )
  reCheckActiveView()
}

const handleOnLoadOffice = async (trigger) => {
  // Boot marker: one line per genuine frame init. resolveOffice() only calls us once
  // Office.context is populated (see loadOfficePromise), so reaching here means a ready
  // context — `trigger` records whether onReady or the readiness poll got us here. `diagnostics`
  // is logged alongside the legacy `host` because on the web the two can diverge. FRAME_ID is
  // passed explicitly because captureOfficePlatform() hasn't run yet, so the isPptOnlineAddin()-
  // gated frame-id prefix isn't active on this earliest line.
  logRoute(
    'frame boot | id =', FRAME_ID,
    '| trigger =', trigger,
    '| host =', window.Office?.context?.host,
    '| diagnostics.host =', window.Office?.context?.diagnostics?.host,
    '| platform =', window.Office?.context?.platform,
    '| href =', window.location.href,
  )
  // Mounted here because this is the earliest point at which the frame is known to be an Office
  // add-in frame: paints FRAME_ID onto the frame so an id in the console can be matched to the
  // frame actually on screen. No-op unless this is a debug build with the namespace enabled.
  mountFrameIdBadge()
  initOffice = true
  // Accept diagnostics.host too: on PowerPoint web the legacy Office.context.host can be blank
  // while Office.context.diagnostics.host is populated. Kept consistent with isOfficeContextReady.
  if (window.Office?.context?.host || window.Office?.context?.diagnostics?.host) {
    store.state.isPowerPointAddIn = true
    if (window._historyCache) {
      window.history.replaceState = window._historyCache.replaceState
      window.history.pushState = window._historyCache.pushState
      window.history.back = window._historyCache.back
    }
    // Wait for Unleash to be ready so the store's feature-flag mirror is populated
    // before handleDataAfterLoadOffice() reads canUsePptDefaultPresentation (AHA-46192)
    // and before detectActiveViewChange() reads the auto-start flag. The add-in entry
    // from App.vue (non-/presentation/ route) has no other ready gate, so without this
    // the flag reads race the fetch. Placed after `initOffice = true` so the dedup
    // guard in resolveOffice still blocks the 2s setTimeout fallback from re-entering.
    // Bounded by a timeout: UnleashService.ready only resolves on the client's 'ready'
    // event, so a stalled proxy would otherwise hang add-in routing (including the
    // flag-off auto-start path) forever. On timeout we proceed with whatever flags are
    // mirrored so far — the same graceful-degradation the fetch-error path already gives.
    let readyTimer
    const unleashWait = await Promise.race([
      UnleashService.ready.then(() => 'ready'),
      new Promise((resolve) => { readyTimer = setTimeout(() => resolve('timeout'), UNLEASH_READY_TIMEOUT_MS) }),
    ])
    clearTimeout(readyTimer)
    // Which arm won, not just that time passed. Without this the only evidence that the flags
    // were never fetched is the size of the +Nms delta on the next line of this namespace —
    // and 'ready' resolving is NOT the same as the flag being on, because the frontend API only
    // returns ENABLED toggles: an absent flag reads `undefined` either way. The mirrored count
    // separates them — 0 flags means nothing arrived, a non-zero count means this flag is off.
    logRoute('unleash wait ->', unleashWait, '| flags mirrored =',
      Object.keys(store.state.featureToggles?.features || {}).length)
    // Fire-and-forget, but NEVER silently: this is async and unawaited, so before this a
    // throw/hang inside it (e.g. a stalled host-slide-tag PowerPoint.run on web cold-load)
    // vanished as an unhandled rejection and the frame just sat on the dashboard. Surface it,
    // and chain the resize off its completion (see below).
    // AHA-46375: the resize (normalise this frame's shape to full-bleed) is sequenced AFTER
    // cold-load, NOT parallel: on web the cold-load host-slide-tag read and the resize each open a
    // PowerPoint.run batch, and two concurrent batches in one frame deadlock the tag read's
    // context.sync() (it never settles, stranding the frame). Chaining resize off cold-load keeps
    // a single PowerPoint.run in flight at a time. Desktop cold-load has no tag-read batch, so
    // this resolves immediately there — timing is unchanged. The whole chain is fire-and-forget;
    // the leading .catch surfaces a cold-load throw and the trailing .catch keeps the (never-
    // rejecting) resize from ever floating a rejection.
    // AHA-43111: settle the landing route before the resize, and in a .finally so a throw
    // above still releases the frame. A cold load that ends without navigating anywhere is
    // the normal "nothing was bound" outcome, not an error — but either way the frame must
    // not be left waiting on a spinner for an answer that is never coming.
    handleDataAfterLoadOffice()
      .catch((error) => logRoute('handleDataAfterLoadOffice threw', error))
      .finally(() => settleAddinLandingRoute())
      .finally(() => resizeAddinToFillSlide())
      .catch(() => {})
    detectActiveViewChange()

    try {
      // initWS()
    } catch (error) {
      console.log('cannot connected powerpoint socket', error)
      // do nothing
    }
  }
}

// A frame is only ready to initialise once Office has populated its context. On PowerPoint for
// the web a multi-frame insert makes Office.onReady fire for some frames but not others
// (AHA-43111); the previous single 2s fallback ran init against a still-empty context on the
// laggards (host/platform undefined), tripping the host guard and stranding the frame on the
// dashboard forever — and because `initOffice` was burned, the real onReady could never recover
// it. Accept diagnostics.host too, since on the web the legacy host property can lag it.
const isOfficeContextReady = () => Boolean(
  window.Office?.context?.host || window.Office?.context?.diagnostics?.host,
)

const OFFICE_JS_SRC = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js'

// Both add-in entry points can run in a single session (App.vue on a non-/presentation/ route,
// then PresentationEditor.vue after a client-side nav to one). Arming readiness twice would leave
// an orphan poll running alongside the real one. loadOffice()'s old `if (window.Office) return`
// short-circuit used to cover that, but it ALSO skipped init entirely whenever office.js was
// already present — which is now the normal case on the add-in build — so the job moves here.
let officeReadinessArmed = false

// Initialises this frame the moment its Office.context is genuinely ready — driven by
// Office.onReady AND a bounded readiness poll so a frame whose onReady never fires is still
// rescued. Only consumes the one-shot `initOffice` on a ready context; gives up after
// OFFICE_READY_MAX_WAIT_MS (resolving without init) so an awaited loadOffice() can never hang.
//
// Requires office.js to already be in the page. Split out of loadOfficePromise so a PRELOADED
// office.js — the PPT add-in build ships it as a static <script> in index.html so the host
// handshake starts during HTML parse instead of after the SPA mounts — reaches init on exactly
// the same path as a runtime-injected one.
//
// `onSettled` is handed in rather than returned as a promise so the whole arming path stays
// synchronous — loadOfficePromise's own resolve reaches Office.onReady in the same tick that
// office.js becomes available, exactly as it did when this lived inline in script.onload.
const armOfficeReadiness = (source, onSettled) => {
  if (officeReadinessArmed) {
    onSettled(window.Office)
    return
  }
  officeReadinessArmed = true

  const resolveOffice = async (trigger) => {
    if (initOffice) return
    if (!isOfficeContextReady()) {
      // Context not populated yet — do NOT burn the one-shot init. A later onReady or poll
      // tick retries once Office finishes wiring up this frame.
      logRoute('resolveOffice deferred (context not ready) | trigger =', trigger)
      return
    }
    log('loaded office', source, '| trigger =', trigger)
    await handleOnLoadOffice(trigger)
    onSettled(window.Office)
  }

  window.Office.onReady(() => resolveOffice('onReady'))

  // Bounded readiness poll replacing the old single 2s setTimeout fallback. Desktop is
  // unaffected — its onReady is prompt, so initOffice is set before the first tick and the
  // poll clears itself.
  let waited = 0
  const readyPoll = setInterval(() => {
    if (initOffice) {
      clearInterval(readyPoll)
      return
    }
    waited += OFFICE_READY_POLL_INTERVAL_MS
    if (isOfficeContextReady()) {
      clearInterval(readyPoll)
      resolveOffice('poll')
      return
    }
    if (waited >= OFFICE_READY_MAX_WAIT_MS) {
      clearInterval(readyPoll)
      logRoute('office context never became ready after', waited, 'ms — resolving without init')
      onSettled(window.Office)
    }
  }, OFFICE_READY_POLL_INTERVAL_MS)
}

// Ensures office.js is present, then arms readiness. On the PPT add-in build index.html has
// already loaded it, so window.Office exists before any bundle code runs and injection is
// skipped; every other build still injects it at runtime exactly as before.
const loadOfficePromise = (source) => new Promise((resolve, reject) => {
  if (window.Office) {
    logRoute('office.js already present (preloaded) — arming readiness without injection')
    armOfficeReadiness(source, resolve)
    return
  }
  const script1 = document.createElement('script')
  script1.onload = () => armOfficeReadiness(source, resolve)
  script1.onerror = reject
  script1.src = OFFICE_JS_SRC
  document.head.appendChild(script1)
})

const loadOffice = async (source = 'unknown') => {
  try {
    // Office.js replaces the default Window.history methods of replaceState and pushState with null. This is done to support older Microsoft webviews and Office versions.
    // But our PowerPoint add-in needs PowerPoint 2019+, which is fully compatible with the new browser APIs.
    // This is the workaround to restore the original methods.
    //
    // Only snapshot when nothing has already done so. On the add-in build index.html takes this
    // exact snapshot BEFORE office.js runs; re-taking it here — after office.js has nulled the
    // methods — would capture nulls, and handleOnLoadOffice() would then "restore" them, breaking
    // vue-router's history mode across the whole add-in.
    if (!window._historyCache) {
      window._historyCache = {
        replaceState: window.history.replaceState,
        pushState: window.history.pushState,
        back: window.history.back,
      }
    }
    await loadOfficePromise(source)
    return window.Office
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

export default {
  loadOffice,
  saveDataToSlidePowerPoint,
  loadDataFromSlidePowerPoint,
  setDataSlideInPowerpointToState,
  setDataPresentationInPowerpointToState,
  handleDataAfterLoadOffice,
  navigateToPresentingSlide,
  clearLastSelectedPresentation,
  clearSlideBindingForCurrentFrame,
  resizeAddinToFillSlide,
  reCheckActiveView,
  detectActiveViewChange,
}
